import { useQuery } from "@tanstack/react-query";

import { useEmployeeIdentifier } from "~/providers/store/employee";
import {
  useKioskDiningOption,
  useKioskSession
} from "~/stores/kiosk-session/hooks/use-kiosk-session";
import { useDiningOption } from "~/providers/store/basket/hooks/use-basket";
import { getKioskUserIdentifier } from "~/utils/tills/get-kiosk-user-identifier";
import { useCurrentSchool } from "~/hooks/queries/schools";

import { useCurrentTill } from "../../tills/hooks/use-till-current";
import { productsQuery } from "../queries";

export function useUserProductsPos({
  enabled = true
}: {
  enabled?: boolean;
} = {}) {
  const { data: till } = useCurrentTill();
  const diningOption = useDiningOption();
  const employeeIdentifier = useEmployeeIdentifier();

  const tillId = till?.id ?? "unknown";
  const isCollectTill = till?.type === "collect";

  return useQuery({
    ...productsQuery({
      tillId,
      userIdentifier: employeeIdentifier,
      diningOption,
      mode: "display",
      include: isCollectTill ? ["collectLimit"] : undefined
    }),
    enabled: Boolean(till?.id) && enabled,
    // Collect: refresh to catch externally-mutated balance (e.g. parent top-up).
    staleTime: isCollectTill ? 0 : 1000 * 60 * 5,
    refetchInterval: isCollectTill ? 1000 * 30 : false,
    gcTime: 1000 * 60 * 10
  });
}

export function useUserProductsKiosk() {
  const { data: till } = useCurrentTill();
  const { data: locationGroupNames } = useCurrentSchool(
    (school) => school.groupNames
  );

  const diningOption = useKioskDiningOption();
  const childId = useKioskSession((session) => session?.childId ?? null);

  const tillId = till?.id ?? "unknown";

  return useQuery({
    ...productsQuery({
      tillId,
      userIdentifier:
        till?.type === "kiosk"
          ? getKioskUserIdentifier(till, locationGroupNames, childId)
          : null,
      diningOption,
      mode: "display"
    }),
    enabled: Boolean(till?.id),
    staleTime: 0,
    refetchInterval: 1000 * 30
  });
}
